import React, { useEffect, useState } from "react";
import API_URL from "./config";
import { getTeamTheme } from "./teamTheme";
import HelpTooltip from "./HelpTooltip";
import "./TradeModal.css";

// ── Player value formula (mirrors MyTeam.js playerOverall) ──────────
const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
const playerValue = (p) => {
  const offense = avg([p.close, p.mid, p.three, p.free_throw, p.dunk, p.passing, p.dribbling, p.offensive_iq]);
  const defense = avg([p.defensive_iq, p.rebounding, p.strength, p.jumping]);
  const athletic = avg([p.speed, p.stamina, p.jumping]);
  return Math.round(offense * 0.45 + defense * 0.35 + athletic * 0.2);
};

const fairnessLabel = (diff) => {
  if (diff <= 2)  return { text: "Even Trade",   color: "#4ade80" };
  if (diff <= 6)  return { text: "Slight Edge",  color: "#facc15" };
  if (diff <= 12) return { text: "Uneven Trade", color: "#fb923c" };
  return               { text: "Lopsided",       color: "#f87171" };
};

export default function TradeModal({ myRoster, myTeamId, onClose, onTradeComplete }) {
  const [step, setStep] = useState(1); // 1=pick my player, 2=pick team, 3=pick their player, 4=confirm
  const [teams, setTeams] = useState([]);
  const [myPlayer, setMyPlayer] = useState(null);
  const [targetTeam, setTargetTeam] = useState(null);
  const [theirRoster, setTheirRoster] = useState([]);
  const [theirPlayer, setTheirPlayer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // "accepted" | "rejected"
  const [stamina, setStamina] = useState(0);
  const [myPlayerSort, setMyPlayerSort] = useState("name"); // "name" or "ovr"
  const [theirPlayerSort, setTheirPlayerSort] = useState("name"); // "name" or "ovr"
  const [teamSort, setTeamSort] = useState("name"); // "name" or "wins"

  // Fetch stamina on mount
  useEffect(() => {
    const activeSaveId = localStorage.getItem("activeSaveId");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (activeSaveId && user._id) {
      fetch(`${API_URL}/api/saves/${activeSaveId}`, {
        headers: { "X-User-ID": user._id },
      })
        .then((res) => res.json())
        .then((data) => {
          setStamina(data.save.stamina || 0);
        })
        .catch(() => {
          setStamina(0);
        });
    }
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  // Fetch all teams for step 2
  useEffect(() => {
    if (step !== 2) return;
    const fetchTeams = async () => {
      setLoading(true);
      try {
        const activeSaveId = localStorage.getItem("activeSaveId");
        const res = await fetch(`${API_URL}/api/standings?save_id=${activeSaveId}`);
        const data = await res.json();
        const s = data.standings ?? data;
        // Flatten all conferences into one list
        const all = Array.isArray(s)
          ? s
          : Object.values(s).flat();
        setTeams(all.filter(t => t._id !== myTeamId));
      } catch (e) {
        setError("Could not load teams.");
      } finally {
        setLoading(false);
      }
    };
    fetchTeams();
  }, [step]);

  // Fetch their roster for step 3
  useEffect(() => {
    if (step !== 3 || !targetTeam) return;
    const fetchRoster = async () => {
      setLoading(true);
      try {
        const activeSaveId = localStorage.getItem("activeSaveId");
        const res = await fetch(`${API_URL}/api/teams/${targetTeam._id}/roster?save_id=${activeSaveId}`);
        const data = await res.json();
        setTheirRoster(data.roster || []);
      } catch (e) {
        setError("Could not load their roster.");
      } finally {
        setLoading(false);
      }
    };
    fetchRoster();
  }, [step, targetTeam]);

  const confirmTrade = async () => {
    if (stamina < 1) {
      setError("Not enough stamina to trade. You need 1 stamina to make a trade.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const activeSaveId = localStorage.getItem("activeSaveId");
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(`${API_URL}/api/saves/${activeSaveId}/trade`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-ID": user._id,
        },
        body: JSON.stringify({
          myPlayerId: myPlayer._id,
          theirPlayerId: theirPlayer._id,
          myTeamId,
          theirTeamId: targetTeam._id,
          isOpponentInitiated: false,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Trade failed.");
        setLoading(false);
        return;
      }
      // Update stamina regardless of trade outcome (both accepted and rejected consume stamina)
      if (data.stamina !== undefined) {
        setStamina(data.stamina);
      }
      setResult(data.status); // "accepted" or "rejected"
      setStep(5);
    } catch (e) {
      setError("Server error during trade.");
    } finally {
      setLoading(false);
    }
  };

  const myVal   = myPlayer   ? playerValue(myPlayer)   : 0;
  const theirVal = theirPlayer ? playerValue(theirPlayer) : 0;
  const diff    = Math.abs(myVal - theirVal);
  const fairness = fairnessLabel(diff);

  // Sort function for players
  const sortPlayers = (players, sortBy) => {
    const sorted = [...players];
    if (sortBy === "ovr") {
      return sorted.sort((a, b) => playerValue(b) - playerValue(a));
    } else {
      return sorted.sort((a, b) => (a.displayName || a.name).localeCompare(b.displayName || b.name));
    }
  };

  // Sort function for teams
  const sortTeams = (teamList, sortBy) => {
    const sorted = [...teamList];
    if (sortBy === "wins") {
      return sorted.sort((a, b) => (b.wins ?? 0) - (a.wins ?? 0));
    } else {
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
  };

  return (
    <div className="trade-overlay" onClick={onClose}>
      <div className="trade-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="trade-modal-header">
          <h2 className="trade-modal-title">Trade Center</h2>
          <button className="trade-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Step indicators */}
        <div className="trade-steps">
          {["My Player", "Team", "Their Player", "Confirm"].map((label, i) => (
            <div key={i} className={`trade-step ${step > i + 1 ? "done" : ""} ${step === i + 1 ? "active" : ""}`}>
              <span className="step-dot">{step > i + 1 ? "✓" : i + 1}</span>
              <span className="step-label">{label}</span>
            </div>
          ))}
        </div>

        <div className="trade-modal-body">
          {error && <div className="trade-error">{error}</div>}
          {loading && <div className="trade-loading">Loading...</div>}

          {/* ── Step 1: Pick my player ── */}
          {step === 1 && (
            <div className="trade-pick-section">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <p className="trade-instruction" style={{ margin: 0 }}>Select a player to trade away:</p>
                <button
                  className="trade-sort-btn"
                  onClick={() => setMyPlayerSort(myPlayerSort === "name" ? "ovr" : "name")}
                >
                  {myPlayerSort === "name" ? "Sort: Name" : "Sort: OVR"}
                </button>
              </div>
              <div className="trade-player-list">
                {sortPlayers(myRoster, myPlayerSort).map(p => (
                  <div
                    key={p._id}
                    className={`trade-player-row ${myPlayer?._id === p._id ? "selected" : ""}`}
                    onClick={() => setMyPlayer(p)}
                  >
                    <span className="trade-pos-badge">{p.position || "?"}</span>
                    <span className="trade-player-name">{p.displayName || p.name}</span>
                    <span className="trade-player-ovr">OVR {playerValue(p)}</span>
                  </div>
                ))}
              </div>
              <div className="trade-nav-row">
                <button className="trade-next-btn" disabled={!myPlayer} onClick={() => setStep(2)}>Next →</button>
              </div>
            </div>
          )}

          {/* ── Step 2: Pick target team ── */}
          {step === 2 && (
            <div className="trade-pick-section">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <p className="trade-instruction" style={{ margin: 0 }}>Select a team to trade with:</p>
                <button
                  className="trade-sort-btn"
                  onClick={() => setTeamSort(teamSort === "name" ? "wins" : "name")}
                >
                  {teamSort === "name" ? "Sort: Name" : "Sort: Wins"}
                </button>
              </div>
              <div className="trade-player-list">
                {sortTeams(teams, teamSort).map(t => {
                  const teamColors = getTeamTheme(t.name);
                  return (
                  <div
                    key={t._id}
                    className={`trade-player-row ${targetTeam?._id === t._id ? "selected" : ""}`}
                    onClick={() => setTargetTeam(t)}
                  >
                    <span className="trade-team-initial" style={{ background: `linear-gradient(135deg, ${teamColors.primary}, ${teamColors.secondary})` }}>{t.name?.charAt(0)}</span>
                    <span className="trade-player-name">{t.name}</span>
                    <span className="trade-player-ovr">{t.wins ?? 0}W - {t.losses ?? 0}L</span>
                  </div>
                );
                })}
              </div>
              <div className="trade-nav-row">
                <button className="trade-back-btn" onClick={() => setStep(1)}>← Back</button>
                <button className="trade-next-btn" disabled={!targetTeam} onClick={() => setStep(3)}>Next →</button>
              </div>
            </div>
          )}

          {/* ── Step 3: Pick their player ── */}
          {step === 3 && (
            <div className="trade-pick-section">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <p className="trade-instruction" style={{ margin: 0 }}>Select a player to receive from <strong>{targetTeam?.name}</strong>:</p>
                <button
                  className="trade-sort-btn"
                  onClick={() => setTheirPlayerSort(theirPlayerSort === "name" ? "ovr" : "name")}
                >
                  {theirPlayerSort === "name" ? "Sort: Name" : "Sort: OVR"}
                </button>
              </div>
              <div className="trade-player-list">
                {sortPlayers(theirRoster, theirPlayerSort).map(p => {
                  const targetTeamColors = getTeamTheme(targetTeam?.name);
                  return (
                  <div
                    key={p._id}
                    className={`trade-player-row ${theirPlayer?._id === p._id ? "selected" : ""}`}
                    onClick={() => setTheirPlayer(p)}
                  >
                    <span className="trade-pos-badge" style={{ background: `linear-gradient(135deg, ${targetTeamColors.primary}, ${targetTeamColors.secondary})` }}>{p.position || "?"}</span>
                    <span className="trade-player-name">{p.displayName || p.name}</span>
                    <span className="trade-player-ovr">OVR {playerValue(p)}</span>
                    {p.injury && <span className="trade-injured-tag">🏥 {p.injury}</span>}
                  </div>
                );
                })}
              </div>
              <div className="trade-nav-row">
                <button className="trade-back-btn" onClick={() => setStep(2)}>← Back</button>
                <button className="trade-next-btn" disabled={!theirPlayer} onClick={() => setStep(4)}>Next →</button>
              </div>
            </div>
          )}

          {/* ── Step 4: Confirm ── */}
          {step === 4 && (() => {
            const targetTeamColors = getTeamTheme(targetTeam?.name);
            return (
            <div className="trade-confirm-section">
              <div className="trade-matchup">
                <div className="trade-card">
                  <span className="trade-card-label">You Give</span>
                  <span className="trade-card-name">{myPlayer?.displayName || myPlayer?.name}</span>
                  <span className="trade-card-pos">{myPlayer?.position}</span>
                  <span className="trade-card-ovr">OVR {myVal}</span>
                </div>
                <div className="trade-arrow">⇄</div>
                <div className="trade-card" style={{ borderTop: `3px solid ${targetTeamColors.primary}` }}>
                  <span className="trade-card-label">You Get</span>
                  <span className="trade-card-name">{theirPlayer?.displayName || theirPlayer?.name}</span>
                  <span className="trade-card-pos">{theirPlayer?.position}</span>
                  <span className="trade-card-ovr">OVR {theirVal}</span>
                  {theirPlayer?.injury && <span className="trade-card-injured">🏥 {theirPlayer.injury}</span>}
                </div>
              </div>

              <div className="trade-fairness" style={{ color: fairness.color }}>
                {fairness.text} — Value difference: {diff} pts
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", overflow: "visible" }}>
                <div className="trade-note">
                  The other team will evaluate this trade. Lopsided trades are more likely to be rejected.
                </div>
                <HelpTooltip 
                  text="Trade fairness based on player overall ratings. Even trades are most likely accepted. Lopsided trades favoring you will likely be rejected."
                  position="top"
                />
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <span style={{ fontSize: "0.9rem", fontWeight: "600", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", minWidth: "60px" }}>Stamina</span>
                  <div style={{ display: "flex", gap: "0.5rem", flex: 1 }}>
                    {[0, 1, 2].map((index) => {
                      let bgColor, shadow, borderColor, animation;
                      if (index < stamina - 1) {
                        bgColor = "#4caf50";
                        shadow = "0 0 12px rgba(76, 175, 80, 0.4)";
                        borderColor = "#4caf50";
                        animation = "none";
                      } else if (index === stamina - 1 && stamina > 0) {
                        bgColor = "#fbbf24";
                        shadow = "0 0 12px rgba(251, 191, 36, 0.6)";
                        borderColor = "#fbbf24";
                        animation = "staminaPulse 2s ease-in-out infinite";
                      } else {
                        bgColor = "rgba(76, 175, 80, 0.15)";
                        shadow = "none";
                        borderColor = "#374151";
                        animation = "none";
                      }
                      return (
                        <div
                          key={index}
                          style={{
                            flex: 1,
                            height: "20px",
                            borderRadius: "4px",
                            transition: "all 0.3s ease",
                            border: `2px solid ${borderColor}`,
                            background: bgColor,
                            boxShadow: shadow,
                            animation: animation
                          }}
                        />
                      );
                    })}
                  </div>
                  <span style={{ fontSize: "0.95rem", fontWeight: "700", color: stamina < 1 ? "#f87171" : "#fff", minWidth: "45px", textAlign: "right" }}>{stamina}/3</span>
                </div>
              </div>

              <div className="trade-note">
                Rejected trades still consume stamina.
              </div>

              <div className="trade-nav-row">
                <button className="trade-back-btn" onClick={() => setStep(3)}>← Back</button>
                <button className="trade-confirm-btn" onClick={confirmTrade} disabled={loading || stamina < 1}>
                  {loading ? "Processing..." : stamina < 1 ? "Need Stamina" : "Submit Trade"}
                </button>
              </div>
            </div>
            );
          })()}

          {/* ── Step 5: Result ── */}
          {step === 5 && (
            <div className="trade-result-section">
              {result === "accepted" ? (
                <>
                  <div className="trade-result-icon accepted">✓</div>
                  <h3 className="trade-result-title accepted">Trade Accepted!</h3>
                  <p className="trade-result-desc">
                    <strong>{theirPlayer?.displayName || theirPlayer?.name}</strong> has joined your team.<br />
                    <strong>{myPlayer?.displayName || myPlayer?.name}</strong> has been traded to {targetTeam?.name}.
                  </p>
                </>
              ) : (
                <>
                  <div className="trade-result-icon rejected">✕</div>
                  <h3 className="trade-result-title rejected">Trade Rejected</h3>
                  <p className="trade-result-desc">
                    {targetTeam?.name} declined the offer. Try a more balanced trade.
                  </p>
                </>
              )}
              <button className="trade-confirm-btn" onClick={() => (onTradeComplete ? onTradeComplete() : onClose())}>Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}